import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const WAReportsAdmin = () => {
  const [dateRange, setDateRange] = useState('30d');
  const [activeReport, setActiveReport] = useState('delivery');
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/wa/admin/reports/stats?range=${dateRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data || {});
      }
    } catch (err) {
      console.error('Failed to fetch report stats:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  const reportTabs = [
    { key: 'delivery', label: 'Delivery Report', icon: 'CheckCheck' },
    { key: 'billing', label: 'Billing & Cost', icon: 'Receipt' },
    { key: 'quality', label: 'Quality & Health', icon: 'Shield' },
    { key: 'conversations', label: 'Conversations', icon: 'MessageCircle' },
  ];

  const renderEmptyState = (icon, title, desc) => (
    <div className="text-center py-12">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
        {getIcon(icon, 32)}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500">{desc}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
              <Icons.BarChart3 className="w-5 h-5 text-white" />
            </div>
            WhatsApp Reports (Admin)
          </h1>
          <p className="text-sm text-gray-500 mt-1">Platform-wide WhatsApp analytics across all WABA accounts</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-xl border border-gray-200 overflow-hidden">
            {['7d', '30d', '90d', 'custom'].map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className="px-3 py-2 text-xs font-medium transition-all"
                style={{
                  background: dateRange === range ? '#25D366' : 'white',
                  color: dateRange === range ? 'white' : '#6b7280',
                }}
              >
                {range === 'custom' ? 'Custom' : range}
              </button>
            ))}
          </div>
          <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
            <Icons.Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Platform-wide Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Messages', value: stats.total_messages?.toLocaleString() || '0', icon: 'Send', color: '#25D366' },
          { label: 'Total Conversations', value: stats.total_conversations?.toLocaleString() || '0', icon: 'MessageCircle', color: '#128C7E' },
          { label: 'Active WABA', value: stats.active_waba?.toString() || '0', icon: 'Building2', color: '#34B7F1' },
          { label: 'Total Cost', value: stats.total_cost ? `₹${stats.total_cost.toLocaleString()}` : '₹0', icon: 'IndianRupee', color: '#075E54' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md transition-all">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}14`, color: stat.color }}>
                {getIcon(stat.icon, 20)}
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Report Tabs */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {reportTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveReport(tab.key)}
              className="flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-all border-b-2 whitespace-nowrap"
              style={{
                borderColor: activeReport === tab.key ? '#25D366' : 'transparent',
                color: activeReport === tab.key ? '#25D366' : '#6b7280',
                background: activeReport === tab.key ? 'rgba(37, 211, 102, 0.04)' : 'transparent',
              }}
            >
              {getIcon(tab.icon, 16)}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
              <p className="text-sm text-gray-500">Loading report data...</p>
            </div>
          ) : (
            <>
              {activeReport === 'delivery' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      { label: 'Sent', value: stats.delivery?.sent?.toLocaleString() || '0', color: '#25D366' },
                      { label: 'Delivered', value: stats.delivery?.delivered?.toLocaleString() || '0', color: '#128C7E' },
                      { label: 'Read', value: stats.delivery?.read?.toLocaleString() || '0', color: '#34B7F1' },
                      { label: 'Failed', value: stats.delivery?.failed?.toLocaleString() || '0', color: '#ef4444' },
                    ].map((s) => (
                      <div key={s.label} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                        <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {(!stats.delivery || stats.delivery.sent === 0) &&
                    renderEmptyState('CheckCheck', 'No Delivery Data', 'Delivery statistics will appear once messages are sent through WhatsApp Business API.')
                  }
                </div>
              )}

              {activeReport === 'billing' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h3 className="font-semibold text-gray-900 mb-4">Cost by Category</h3>
                      {(!stats.billing || !stats.billing.categories) ? (
                        <p className="text-sm text-gray-500 text-center py-4">No billing data available yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {(stats.billing.categories || []).map((cat) => (
                            <div key={cat.name} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                              <div>
                                <p className="text-sm font-medium text-gray-900 capitalize">{cat.name}</p>
                                <p className="text-xs text-gray-400">{(cat.count || 0).toLocaleString()} conversations</p>
                              </div>
                              <p className="text-sm font-bold text-gray-900">₹{(cat.cost || 0).toLocaleString()}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h3 className="font-semibold text-gray-900 mb-4">Cost by WABA Account</h3>
                      {(!stats.billing || !stats.billing.by_waba) ? (
                        <p className="text-sm text-gray-500 text-center py-4">No per-account billing data yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {(stats.billing.by_waba || []).map((w) => (
                            <div key={w.waba_id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{w.name || `WABA #${w.waba_id}`}</p>
                                <p className="text-xs text-gray-400">{(w.conversations || 0).toLocaleString()} conversations</p>
                              </div>
                              <p className="text-sm font-bold text-gray-900">₹{(w.cost || 0).toLocaleString()}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeReport === 'quality' && (
                <div className="space-y-6">
                  {(!stats.quality || !stats.quality.accounts) ? (
                    renderEmptyState('Shield', 'No Quality Data', 'Quality metrics will appear once WABA accounts are active and sending messages.')
                  ) : (
                    <div className="space-y-3">
                      {(stats.quality.accounts || []).map((acc) => (
                        <div key={acc.waba_id} className="p-4 rounded-xl border border-gray-100 hover:border-green-200 transition-all">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
                                <Icons.Building2 className="w-5 h-5" style={{ color: '#25D366' }} />
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900 text-sm">{acc.name || `WABA #${acc.waba_id}`}</h3>
                                <p className="text-xs text-gray-400">Tier: {acc.messaging_limit_tier || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-center">
                                <div className={`w-4 h-4 rounded-full mx-auto ${acc.quality_rating === 'GREEN' ? 'bg-green-500' : acc.quality_rating === 'YELLOW' ? 'bg-yellow-500' : acc.quality_rating === 'RED' ? 'bg-red-500' : 'bg-gray-400'}`} />
                                <p className="text-[10px] text-gray-500 mt-1">Quality</p>
                              </div>
                              <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-600">{acc.status || 'unknown'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeReport === 'conversations' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      { label: 'Total', value: stats.conversations?.total?.toLocaleString() || '0', color: '#25D366' },
                      { label: 'Business Initiated', value: stats.conversations?.business_initiated?.toLocaleString() || '0', color: '#128C7E' },
                      { label: 'User Initiated', value: stats.conversations?.user_initiated?.toLocaleString() || '0', color: '#34B7F1' },
                      { label: 'Avg Resolution', value: stats.conversations?.avg_resolution || 'N/A', color: '#075E54' },
                    ].map((s) => (
                      <div key={s.label} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                        <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {(!stats.conversations || stats.conversations.total === 0) &&
                    renderEmptyState('MessageCircle', 'No Conversation Data', 'Conversation analytics will populate as WhatsApp interactions occur.')
                  }
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WAReportsAdmin;
